import type { Dayjs } from 'dayjs';
import type { FieldPath, UseFormSetValue } from 'react-hook-form';
import { MatriculaAlunoSchema } from '@/schema/matriculaAlunoSchema';
import { formatToBr, parseAnyToDayjs } from '@/utils/dateUtils';

/**
 * Preenchimento da tela a partir da consulta por CPF.
 *
 * O CPF não é obrigatório, mas identifica a Pessoa: ao informá-lo, a tela só
 * pode mudar quando existe registro com aquele CPF. Por isso o preenchimento é
 * um *merge* — campo sem valor na base não sobrescreve o que está na tela.
 * Antes daqui cada passo fazia `setValue(campo, pessoa.campo || '')`, o que
 * zerava tudo o que a API não devolvia (inclusive o passo Aluno, escrito pelo
 * passo Responsável) e ainda gravava esses vazios no submit.
 */

/** Campos de Pessoa que os passos Responsável e Aluno editam. */
export const CAMPOS_PESSOA = [
  'cpfCnpj',
  'nome',
  'rg',
  'orgaoEmissorRg',
  'ufRg',
  'email',
  'cep',
  'uf',
  'cidade',
  'bairro',
  'logradouro',
  'numero',
  'complemento',
  'telefone',
  'celular',
] as const;

export type CampoPessoa = typeof CAMPOS_PESSOA[number];

export type PrefixoPessoa = 'responsavel.pessoaResponsavel' | 'estudante.pessoaEstudante';

type ResultadoPreenchimento = {
  /** Data de nascimento vinda da base, para o passo sincronizar seu DatePicker. */
  dataNascimento: Dayjs | null;
  /** Só os campos que a base tinha preenchidos — usado para os estados locais. */
  aplicados: Partial<Record<CampoPessoa, string>>;
};

/** A base só sobrescreve a tela quando tem valor de verdade. */
export function temValor(valor: unknown): boolean {
  return valor !== null && valor !== undefined && String(valor).trim() !== '';
}

export function preencherPessoaNoForm(
  setValue: UseFormSetValue<MatriculaAlunoSchema>,
  prefixo: PrefixoPessoa,
  pessoa: any,
  opcoes: { ignorar?: readonly CampoPessoa[] } = {},
): ResultadoPreenchimento {
  const aplicados: Partial<Record<CampoPessoa, string>> = {};

  if (!pessoa) {
    return { dataNascimento: null, aplicados };
  }

  CAMPOS_PESSOA.forEach((campo) => {
    if (opcoes.ignorar?.includes(campo)) return;
    if (!temValor(pessoa[campo])) return;

    const valor = String(pessoa[campo]);
    definirCampo(setValue, `${prefixo}.${campo}`, valor);
    aplicados[campo] = valor;
  });

  // Sem data válida na base o campo fica como está: `formatToBr` devolve string
  // vazia para nulo, e era ela que limpava a data já preenchida na tela.
  const dataNascimento = parseAnyToDayjs(pessoa.dataNascimento);
  if (dataNascimento) {
    definirCampo(setValue, `${prefixo}.dataNascimento`, formatToBr(dataNascimento));
  }

  return { dataNascimento, aplicados };
}

/**
 * O caminho é montado por concatenação, então o TypeScript não consegue casar o
 * par caminho/valor dos overloads do `setValue` — todos os campos envolvidos são
 * `string | null | undefined` no schema.
 */
function definirCampo(
  setValue: UseFormSetValue<MatriculaAlunoSchema>,
  caminho: string,
  valor: string,
): void {
  setValue(caminho as FieldPath<MatriculaAlunoSchema>, valor as never);
}
